import { useCallback, useLayoutEffect, useRef } from 'react';
import { useAskAicaSurface } from '../../context/AskAicaSurfaceContext';
import { LOAD_OLDER_SCROLL_THRESHOLD } from '../../../../../../../../store/globalAskAica/whatsapp/constants';

/**
 * Scroll behaviour for the WhatsApp view: land on the newest message when a chat opens,
 * and keep the reader's place when an older page is added above.
 */
export const useWhatsAppChatViewLogic = () => {
    const { whatsApp } = useAskAicaSurface();
    const containerRef = useRef<HTMLDivElement>(null);
    // Scroll height before an older page was requested; only meaningful while that request is in flight.
    const prevHeightRef = useRef<number | null>(null);
    const items = whatsApp?.items;
    const activeChatId = whatsApp?.activeChatId;
    const isLoading = whatsApp?.isLoading;
    const olderError = whatsApp?.olderError;

    const loadOlder = useCallback(() => {
        if (!whatsApp?.hasMore || whatsApp.isLoadingOlder) return;
        prevHeightRef.current = containerRef.current?.scrollHeight ?? null;
        whatsApp.loadOlder();
    }, [whatsApp]);

    // Drop the anchor only on real invalidations, never merely because isLoadingOlder is false:
    // React batches the older items and isLoadingOlder=false into one render, and the scroll effect
    // below must still see the anchor then. Invalidations: another chat opened or closed, a reset
    // or (re)open raised isLoading (this also covers a discarded older response), or the older load
    // failed. Declared before the scroll effect so an open always lands on the newest message.
    useLayoutEffect(() => {
        prevHeightRef.current = null;
    }, [activeChatId]);

    useLayoutEffect(() => {
        if (isLoading || olderError) prevHeightRef.current = null;
    }, [isLoading, olderError]);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        if (prevHeightRef.current !== null) {
            el.scrollTop += el.scrollHeight - prevHeightRef.current;
            prevHeightRef.current = null;
        } else {
            el.scrollTop = el.scrollHeight;
        }
    }, [items]);

    // After a failure only the button retries, so a scroll at the top cannot hammer a failing endpoint.
    const handleScroll = useCallback(() => {
        if (olderError) return;
        if (containerRef.current && containerRef.current.scrollTop <= LOAD_OLDER_SCROLL_THRESHOLD) loadOlder();
    }, [loadOlder, olderError]);

    return { whatsApp, containerRef, handleScroll, loadOlder };
};
